// src/components/sections/ProjectsSection.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, scaleIn } from '../../styles/animations';
import SectionTitle from '../ui/SectionTitle';
import { realizationsService } from '../../services/api';

const ProjectsWrapper = styled.section`
  background-color: ${props => props.theme.colors.gray};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const ProjectsIntro = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: all ${props => props.theme.transitions.medium};
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    .project-image img {
      transform: scale(1.05);
    }
  }
`;

const ProjectImage = styled.div`
  height: 240px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
`;

const ProjectContent = styled.div`
  padding: ${props => props.theme.spacing.md};
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
`;

const ProjectLocation = styled.span`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.sm};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProjectLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  margin-top: ${props => props.theme.spacing.sm};
  transition: all ${props => props.theme.transitions.fast};
  
  svg {
    margin-left: 0.5rem;
    transition: transform ${props => props.theme.transitions.fast};
  }
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    
    svg {
      transform: translateX(5px);
    }
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.light};
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-top: ${props => props.theme.spacing.lg};
  transition: background-color ${props => props.theme.transitions.fast};
  text-decoration: none;
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    color: ${props => props.theme.colors.light};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.lg};
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.6s;
  opacity: 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.lg} 0;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.lg} 0;
  color: ${props => props.theme.colors.danger};
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const ProjectsSection = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Charger les réalisations
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                setError(null);

                // Paramètres pour l'API - on prend juste les 6 derniers projets
                const params = {
                    page: 1,
                    limit: 6,
                    sort: 'created_at:desc' // Les plus récents d'abord
                };

                const response = await realizationsService.getAll(params);

                // Mise à jour de l'état avec les données reçues
                if (response) {
                    if (response.realizations) {
                        setProjects(response.realizations);
                    } else if (Array.isArray(response)) {
                        setProjects(response);
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error('Erreur lors du chargement des réalisations:', err);
                setError('Erreur lors du chargement des réalisations');
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    return (
        <ProjectsWrapper id="realisations">
            <ProjectsContainer>
                <ProjectsIntro>
                    <SectionTitle
                        text="Nos réalisations"
                        subtitle="Découvrez quelques exemples de nos créations personnalisées qui embellissent les espaces extérieurs de nos clients."
                        centered
                    />
                </ProjectsIntro>

                {loading ? (
                    <LoadingMessage>Chargement des réalisations...</LoadingMessage>
                ) : error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : projects.length === 0 ? (
                    <ErrorMessage>Aucune réalisation trouvée.</ErrorMessage>
                ) : (
                    <ProjectsGrid>
                        {projects.map((project, index) => (
                            <ProjectCard key={project.id} delay={`${0.1 * index}s`}>
                                <ProjectImage className="project-image">
                                    <img
                                        src={project.featured_image
                                            ? `https://decko.fr/uploads/${project.featured_image}`
                                            : 'https://via.placeholder.com/400x240?text=Decko+Réalisations'}
                                        alt={project.title}
                                    />
                                </ProjectImage>
                                <ProjectContent>
                                    <ProjectHeader>
                                        <ProjectTitle>{project.title}</ProjectTitle>
                                        <ProjectLocation>{project.location || ''}</ProjectLocation>
                                    </ProjectHeader>
                                    {project.description && (
                                        <ProjectDescription>{project.description}</ProjectDescription>
                                    )}
                                    <ProjectLink to={`/realisations/${project.id}`}>
                                        Voir le projet
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                                        </svg>
                                    </ProjectLink>
                                </ProjectContent>
                            </ProjectCard>
                        ))}
                    </ProjectsGrid>
                )}

                <ButtonContainer>
                    <CTAButton to="/realisations">
                        Voir toutes nos réalisations
                    </CTAButton>
                </ButtonContainer>
            </ProjectsContainer>
        </ProjectsWrapper>
    );
};

export default ProjectsSection;